import { Drawer, useMediaQuery, useTheme, Box, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Stack, Chip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import PhoneIcon from '@mui/icons-material/Phone'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useContext, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import kycIconSvg from 'src/assets/kyc approval.svg'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import { preloadImage } from 'src/utils/imagePreloader'
import { logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { ProfileDataComponent } from '../MentorProfilePage'
import { mentorProfileSaveFunction } from '../MentorProfilePage/utils'
import {
  KycIcon,
  KycProceedButton,
  KycExploreAppButton,
  KycSubText,
  KycText,
} from '../../layout/Styles'

// Phase 14e — applicant-facing support contacts. Mirrors the values the backend bakes into
// the submission-confirmation email/WhatsApp (see PractitionerOnboardingNotificationService
// `practitioner-application.support-{phone,whatsapp,review-sla-hours}` properties). Kept in
// sync by convention; if these need to vary per env the right move is fetching them from
// the public config endpoint instead of duplicating here.
const SUPPORT_PHONE_DISPLAY = '+91 80451 23456'
const SUPPORT_PHONE_TEL = '+918045123456'
const SUPPORT_WHATSAPP_DISPLAY = '+91 80451 23456'
const SUPPORT_WHATSAPP_E164 = '918045123456'
const REVIEW_SLA_HOURS = 48

function formatSubmissionTimestamp(rawTimestamp) {
  if (!rawTimestamp) return null
  // Accept ISO string ('2026-04-30T05:30:00Z') and the legacy 'YYYY-MM-DD HH:mm:ss' UTC shape
  // used elsewhere in the codebase. dayjs handles both transparently.
  const parsed = dayjs(rawTimestamp)
  if (!parsed.isValid()) return null
  return parsed.format('DD MMM YYYY, h:mm A')
}

export default function KycPendingActionsPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mentorProfileData, updateProfileData, allLegalPolicies, setDontShowKycModal, refreshLegalPolicies } =
    useContext(MentorProfileContext)
  const { allTags } = useContext(MentorSetupContext)
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)

  const handleEditProfile = () => {
    setProfileDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setProfileDrawerOpen(false)
  }

  const handleExploreApp = () => {
    // Set flags to indicate user explicitly chose consumer portal
    sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
    sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
    sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
    // Navigate to consumer portal (root path)
    window.location.replace('/')
  }

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true)
  }

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false)
    logoutHandlerMentor()
    navigate('/')
  }

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false)
  }

  // Preload KYC SVG immediately on mount for LCP optimization
  useEffect(() => {
    if (kycIconSvg && typeof kycIconSvg === 'string') {
      preloadImage(kycIconSvg, {
        as: 'image',
        fetchpriority: 'high',
      })
    }
  }, [])

  // Phase 14e — surface the submission datetime + a "Just submitted" framing when the
  // applicant has just landed here from /apply's "Submit for review" hand-off (the
  // PostLoginEnrichmentForm sets `practitionerApplicationJustSubmitted` in sessionStorage
  // right before window.location.replace). Stays sticky while they're on this page so a
  // mid-session refresh keeps the friendly framing; clears once they navigate elsewhere
  // (the layout redirects them off this page when KYC is approved).
  const justSubmitted = useMemo(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem('practitionerApplicationJustSubmitted') === '1'
  }, [])

  const submittedAt = useMemo(
    () =>
      formatSubmissionTimestamp(
        mentorProfileData?.firstAppliedTime ||
          mentorProfileData?.submittedAt ||
          mentorProfileData?.createdTime,
      ),
    [mentorProfileData],
  )

  const headlineText = justSubmitted
    ? "We've received your application"
    : 'KYC Approval Pending'
  const subText = justSubmitted
    ? `Thanks for applying to AUMHUM. Our onboarding team will review your application within ${REVIEW_SLA_HOURS} hours and reach out by email and WhatsApp. While you wait you can keep your profile fresh.`
    : 'Your KYC approval is pending. We will send you an email when it is approved. In the meantime, you can edit your profile.'

  return (
    <>
      {/* Use simple Box instead of DialogContent for better LCP performance */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 2, sm: 4 },
          gap: '40px',
          position: 'relative',
        }}
      >
        {/* Logout Button - Top Right */}
        <Box
          sx={{
            position: 'absolute',
            top: { xs: '0.75rem', sm: '1rem' },
            right: { xs: '0.75rem', sm: '1rem' },
          }}
        >
          <Tooltip title="Logout" arrow>
            <IconButton
              onClick={handleLogoutClick}
              sx={{
                color: '#ef4444',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
              aria-label="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <KycIcon
          src={kycIconSvg}
          alt={headlineText}
          loading="eager"
          fetchpriority="high"
          decoding="async"
          width="100"
          height="100"
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            maxWidth: 560,
            textAlign: 'center',
          }}
        >
          {justSubmitted && (
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
              label="Application submitted"
              size="small"
              sx={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                fontSize: '11px',
                mb: 0.5,
              }}
            />
          )}
          <KycText>{headlineText}</KycText>
          <KycSubText>{subText}</KycSubText>
          {submittedAt && (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, color: '#475569', fontSize: '14px' }}
            >
              Submitted on <strong>{submittedAt}</strong>
            </Typography>
          )}
        </Box>

        {/* Phase 14e — applicant support contacts. Pure-CSS card so it renders early
            without waiting on heavier MUI surfaces; phone/WhatsApp links open native
            handlers on mobile and the WhatsApp web client on desktop. */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            padding: '16px 20px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '11px',
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: '#1e3151',
              fontWeight: 700,
            }}
          >
            Need to reach mentor support?
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            sx={{ width: '100%' }}
          >
            <Button
              component="a"
              href={`tel:${SUPPORT_PHONE_TEL}`}
              startIcon={<PhoneIcon />}
              variant="outlined"
              fullWidth
              sx={{
                textTransform: 'none',
                borderColor: '#1e3151',
                color: '#1e3151',
                '&:hover': { borderColor: '#1e3151', backgroundColor: '#eef2ff' },
              }}
            >
              Call {SUPPORT_PHONE_DISPLAY}
            </Button>
            <Button
              component="a"
              href={`https://wa.me/${SUPPORT_WHATSAPP_E164}`}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsAppIcon />}
              variant="contained"
              fullWidth
              sx={{
                textTransform: 'none',
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#1eb957' },
              }}
            >
              WhatsApp {SUPPORT_WHATSAPP_DISPLAY}
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '8px',
            alignItems: 'center',
          }}
        >
          <KycProceedButton onClick={handleEditProfile}>Edit Profile</KycProceedButton>
          <KycExploreAppButton onClick={handleExploreApp}>
            Explore Mental Wellness App
          </KycExploreAppButton>
        </Box>
      </Box>

      {/* Profile Drawer - Right to left on desktop, bottom to top on mobile */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={profileDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : { xs: '100%', sm: '600px', md: '700px', lg: '800px' },
            height: isMobile ? '90vh' : '100vh',
            borderRadius: isMobile ? '16px 16px 0 0' : '0',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        {/* Drawer Header with Close Button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <KycText sx={{ fontSize: '18px', fontWeight: 600 }}>Edit Profile</KycText>
          <IconButton onClick={handleCloseDrawer} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Profile Form Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <ProfileDataComponent
            profileData={mentorProfileData}
            updateProfileData={updateProfileData}
            enteringFirstTime={false}
            allLegalPolicies={allLegalPolicies}
            setDontShowKycModal={setDontShowKycModal}
            refreshLegalPolicies={refreshLegalPolicies}
            formContextData={{ domains: allTags }}
            slugInitialUrl={'https://aumhum.com/mentor/'}
            saveObjectTransform={(formData) => {
              // After save, the ProfileDataComponent will call updateProfileData
              // and then onCancel, which will close the drawer
              return mentorProfileSaveFunction(formData)
            }}
            onCancel={handleCloseDrawer}
          />
        </Box>
      </Drawer>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Confirm Logout
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to logout? You will need to sign in again to access your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1 }}>
          <Button
            onClick={handleLogoutCancel}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none' }}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

